import { toast } from 'react-toastify'

import { SetAccount, TradingAction, SetType } from '../actions'
import { AppState, AppTheme, ChartMode, AppMode, AppType } from '../types'
import * as constants from '../constants'
import { FulfillAssetGroups, FulfillLogin, FulfillPrices, LoginToMt4 } from '../actions/mt4Actions'
import {
  RefreshTradingValues,
  InitApp,
  ClearState,
  ApplyPostCommand,
  SetChartMode,
  SetPositionsMode,
  SetAppMode,
  SetAppTheme,
  SetTradingSymbol,
  SetTradingSignal,
  ClearTradingSignal,
  DismissRiscDisclaimer,
  RefreshChart,
} from '../actions/commonActions'
import { MT4Connector } from '@bdswiss/mt4-connector'
import { getValidSymbol, getSymbolFromGroups, setAppTheme } from '../utils/common-utils'
import { getCookie, setCookie } from '../utils/cookie-utils'

const mt4: MT4Connector = MT4Connector.Instance

export const defaultState: AppState = {
  appType: 'forex' as AppType,
  accountLoaded: false,
  assetLoaded: false,
  positionsOpened: false,
  assetsOpened: true,
  tradingPaneOpened: true,
  editPaneOpened: false,
  sidebarOpened: true,
  currency: 'USD',
  symbol: 'EURUSD',
  activeAccount: '',
  selectedPosition: NaN,
  positionExpanded: false,
  selectedOrder: NaN,
  orderExpanded: false,
  appMode: 'trade' as AppMode,
  chartMode: 'trade' as ChartMode,
  appTheme: (getCookie('theme') || 'Light') as AppTheme,
  dismissedRiscDisclaimer: false,
  refreshChart: false,
}

export function appState(
  state: AppState = { ...defaultState },
  action:
    | TradingAction
    | FulfillLogin
    | FulfillPrices
    | RefreshTradingValues
    | LoginToMt4
    | ClearState
    | InitApp
    | ApplyPostCommand
    | SetAccount
    | FulfillAssetGroups
    | SetType
    | SetChartMode
    | SetPositionsMode
    | SetAppMode
    | SetAppTheme
    | SetTradingSymbol
    | SetTradingSignal
    | ClearTradingSignal
    | DismissRiscDisclaimer
    | RefreshChart
): AppState {
  switch (action.type) {
    case constants.APPLY_POST_COMMAND: {
      const { range } = action
      if (range) {
        //
        // const to = Math.round(new Date().getTime() / 1000) ;
        // const from = to - (timeScale * 60);
        // const range = {from, to, timeScale};
        return { ...state, range }
      } else {
        return { ...state }
      }
    }
    case constants.INIT_APP:
      return {
        ...state,
        activeAccount: action.accountLogin ? action.accountLogin : state.activeAccount,
        symbol: action.defaultSymbol,
        dismissedRiscDisclaimer: action.dismissedRiscDisclaimer,
      }
    case constants.SET_ACCOUNT:
      return { ...state, activeAccount: action.account.login }
    case constants.FULFILL_PRICES:
      return { ...state, symbol: getValidSymbol(action.prices, state.symbol), assetLoaded: true }
    case constants.FULFILL_ASSET_GROUPS:
      return { ...state, symbol: getSymbolFromGroups(action.assetGroups, state.symbol) }
    case constants.CHANGE_SYMBOL:
      mt4.unsubscribeFromSymbol(state.symbol)
      mt4.subscribeToSymbol(action.symbol)
      setCookie('currentSymbol', action.symbol)
      return { ...state, symbol: action.symbol, assetLoaded: false, signal: null }
    case constants.TOGGLE_POSITIONS:
      return { ...state, positionsOpened: !state.positionsOpened }
    case constants.TOGGLE_ASSETS:
      return { ...state, assetsOpened: !state.assetsOpened }
    case constants.TOGGLE_TRADING_PANE:
      const tradingPaneIsOpened = !state.tradingPaneOpened
      return { ...state, tradingPaneOpened: tradingPaneIsOpened }
    case constants.OPEN_EDIT_PANE:
      return {
        ...state,
        tradingPaneOpened: false,
        editPaneOpened: true,
        positionExpanded: true,
        sidebarOpened: true,
      }
    case constants.FULFILL_LOGIN: {
      return { ...state, currency: action.loginSuccess.currency, accountLoaded: true }
    }
    case constants.SHOW_SUCCESS_MESSAGE: {
      toast.success(action.message)
      return state
    }
    case constants.SHOW_ERROR_MESSAGE: {
      toast.error(action.error.message)
      return state
    }
    case constants.SHOW_SOKET_ERROR_MESSAGE: {
      toast.error(action.error.message)
      return state
    }
    case constants.SELECT_ACCOUNT: {
      return { ...defaultState, appTheme: state.appTheme, activeAccount: action.accountId }
    }
    case constants.SELECT_POSITION: {
      let newState: Partial<AppState> = {}
      if (['positions', 'assetPosition'].includes(state.appMode)) {
        newState = {
          selectedPosition: action.order,
          positionExpanded:
            (action.expanded === undefined && state.positionExpanded) || action.expanded,
        }
      } else if (state.appMode === 'orders') {
        newState = {
          selectedOrder: action.order,
          orderExpanded: (action.expanded === undefined && state.orderExpanded) || action.expanded,
        }
      }
      return { ...state, ...newState }
    }
    case constants.SELECT_ORDER: {
      return {
        ...state,
        selectedOrder: action.order,
        orderExpanded: (action.expanded === undefined && state.orderExpanded) || action.expanded,
      }
    }
    case constants.REFRESH_TRADING_VALUES: {
      return { ...state, assetLoaded: true }
    }
    case constants.REFRESH_ACCOUNT_VALUES: {
      return { ...state, accountLoaded: true }
    }
    case constants.SET_ACCOUNT_TYPE: {
      return { ...state, appType: action.appType }
    }
    case constants.SET_CHART_MODE: {
      return { ...state, chartMode: action.mode }
    }
    case constants.REFRESH_CHART: {
      return { ...state, refreshChart: action.refresh }
    }
    case constants.SET_TRADING_SYMBOL: {
      mt4.unsubscribeFromSymbol(state.symbol)
      mt4.subscribeToSymbol(action.symbol)
      setCookie('currentSymbol', action.symbol)
      return { ...state, appMode: 'trade', chartMode: 'trade', symbol: action.symbol }
    }
    case constants.SET_TRADING_SIGNAL: {
      return { ...state, signal: action.signal }
    }
    case constants.CLEAR_TRADING_SIGNAL: {
      return { ...state, signal: null }
    }
    case constants.SET_POSITIONS_MODE: {
      return { ...state, positionsMode: action.mode }
    }
    case constants.SET_APP_THEME: {
      const { theme } = action
      setAppTheme(theme)
      return { ...state, appTheme: theme }
    }
    case constants.SET_APP_MODE: {
      let newState: Partial<AppState> = {}
      const { positionsMode, positionsOpened, assetsOpened } = state
      switch (action.mode) {
        case 'trade':
          newState = {
            assetsOpened: !assetsOpened,
            positionsOpened: false,
            chartMode: 'trade',
            signal: null,
          }
          break
        case 'assetPosition':
          newState = {
            assetsOpened: true,
            positionsOpened: false,
            chartMode: 'open',
            signal: null,
          }
          break
        case 'positions':
          newState = {
            assetsOpened: false,
            positionsMode: 'open',
            positionsOpened:
              (!positionsOpened && positionsMode === 'open') || positionsMode !== 'open',
            chartMode: 'open',
            signal: null,
          }
          break
        case 'orders':
          newState = {
            assetsOpened: false,
            positionsMode: 'pending',
            positionsOpened:
              (!positionsOpened && positionsMode === 'pending') || positionsMode !== 'pending',
            chartMode: 'pending',
          }
          break
        case 'history':
          newState = {
            assetsOpened: false,
            positionsMode: 'closed',
            positionsOpened: true,
            chartMode: 'trade',
            signal: null,
          }
          break
      }
      return { ...state, appMode: action.mode, ...newState }
    }
    case constants.DISMISS_RISC_DISCLAIMER: {
      setCookie('dismissedRiscDisclaimer', 'true')
      return { ...state, dismissedRiscDisclaimer: true }
    }
    default:
      return state
  }
}
